import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Spin,
  Alert,
  Typography,
  Card,
  Tag,
  Row,
  Col,
  Divider,
} from "antd";
import {
  TeamOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import styles from "./ConfirmSMSBlastModal.module.scss";

const { Text, Title, Paragraph } = Typography;

const ConfirmSMSBlastModal = ({
  open,
  onCancel,
  onConfirm,
  propertyName,
  communityNames,
  message,
  isLoading,
  error,
  recipientCount = 0,
  chargeToSendMessage = 0,
  monthToDateCharges = 0,
  unitPrice = 0,
  tierMinMessages = 0,
  tierMaxMessages = null,
}) => {
  const hasRecipients = recipientCount > 0;

  const tierRangeLabel =
    tierMaxMessages === null || tierMaxMessages === undefined
      ? `${tierMinMessages.toLocaleString()} or more`
      : `${tierMinMessages.toLocaleString()}-${tierMaxMessages.toLocaleString()} tier`;
  const rateAppliedLabel = `Rate applied: $${unitPrice.toFixed(3)} per message (${tierRangeLabel})`;

  return (
    <Modal
      data-testid="sms-confirm-modal"
      open={open}
      onCancel={onCancel}
      centered
      width={700}
      destroyOnClose
      footer={null}
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Confirm SMS Blast
          </Title>
          <Text type="secondary">
            Review details before sending.
          </Text>
        </div>
      }
      className={styles["confirm-sms-modal"]}
    >
      {isLoading ? (
        <Spin tip="Calculating delivery costs..." />
      ) : error ? (
        <Alert type="error" message={error} showIcon />
      ) : !hasRecipients ? (
        <Alert
          type="warning"
          showIcon
          message="No recipients found"
          description="Adjust your filters or community selection to continue."
        />
      ) : (
        <div>
          {/* BLAST DETAILS */}
          <Divider style={{ margin: 0 }} />

          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Blast Details
            </Text>

            <Card
              size="small"
              className={styles["card-default"]}
              style={{ marginTop: 8 }}
            >
              <Row gutter={[16, 16]}>
                {/* Property */}
                <Col span={12}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <HomeOutlined style={{ color: "#9ca3af" }} />
                    <div>
                      <Text type="secondary">Target Property</Text>
                      <div>
                        <Text strong>{propertyName || "--"}</Text>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Recipients */}
                <Col span={12}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <TeamOutlined style={{ color: "#9ca3af" }} />
                    <div>
                      <Text type="secondary">Recipients</Text>
                      <div className={styles["recipients-row"]}>
                        <Title level={3} className={styles["card-recipients-title"]}>
                          {recipientCount.toLocaleString()}
                        </Title>
                        <span className={`${styles["recipients-caption"]} ${styles["recipients-caption-desktop"]}`}>
                          Total recipients
                        </span>
                        <span className={`${styles["recipients-caption"]} ${styles["recipients-caption-mobile"]}`}>
                          Total recipientIds
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Communities */}
                <Col span={24}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <HomeOutlined style={{ color: "#9ca3af" }} />
                    <Text type="secondary">Communities</Text>
                  </div>
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {communityNames?.length ? (
                      communityNames.map((c) => (
                        <Tag style={{ borderRadius: 8, background: "#fff" }} key={c}>{c}</Tag>
                      ))
                    ) : (
                      <Text type="secondary">--</Text>
                    )}
                  </div>
                </Col>

                {/* Message */}
                <Col span={24}>
                  <Divider style={{ margin: "12px 0" }} />
                  <div>
                    <Text
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        fontWeight: 500,
                      }}
                      strong={false}
                    >
                      Message Preview
                    </Text>
                    <div className={styles["message-preview"]} style={{ whiteSpace: "pre-wrap" }}>
                      {message || "--"}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </div>

          {/* MONTHLY STATUS */}
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Monthly Status
            </Text>

            <Row gutter={[16, 16]} style={{ marginTop: 8 }} className={styles["monthly-status-row"]}>
              {/* Charge to Send Message */}
              <Col span={12}>
                <Card size="small" className={`${styles["card-default"]} ${styles["monthly-status-card"]}`}>
                  <Text type="secondary">Charge to Send Message</Text>
                  <Title level={3} className={styles["card-title"]}>
                    ${chargeToSendMessage.toFixed(2)}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {rateAppliedLabel}
                  </Text>
                </Card>
              </Col>

              {/* Month-to-Date Charges */}
              <Col span={12}>
                <Card size="small" className={`${styles["card-default"]} ${styles["monthly-status-card"]}`}>
                  <Text type="secondary">Month-to-Date Charges</Text>
                  <Title level={3} className={styles["card-title"]}>
                    ${monthToDateCharges.toFixed(2)}
                  </Title>
                </Card>
              </Col>
            </Row>
          </div>

          {/* FOOTER ACTIONS */}
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button data-testid="sms-modal-cancel-btn" onClick={onCancel}>Cancel</Button>
            <Button
              data-testid="sms-modal-confirm-send-btn"
              type="primary"
              onClick={onConfirm}
              disabled={!hasRecipients}
            >
              Confirm & Send
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

ConfirmSMSBlastModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  propertyName: PropTypes.string,
  communityNames: PropTypes.arrayOf(PropTypes.string),
  message: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  recipientCount: PropTypes.number,
  chargeToSendMessage: PropTypes.number,
  monthToDateCharges: PropTypes.number,
  unitPrice: PropTypes.number,
  tierMinMessages: PropTypes.number,
  tierMaxMessages: PropTypes.number,
};

export default ConfirmSMSBlastModal;